/**
 * Categories Drawer — loads Categories All content from collection.categories.json
 * (via Section Rendering API / page HTML). Reuses hdt-drawer open/close behavior.
 */
(function () {
  var cache = Object.create(null);
  var loading = Object.create(null);
  var SECTION_ID_STORAGE_KEY = "hdtCategoriesSectionId";

  function getDrawer() {
    return document.getElementById("categories_drawer");
  }

  function getStaticSectionKey(drawer) {
    return (
      (drawer && drawer.getAttribute("data-categories-section-id")) ||
      "categories_all_NXHa7P"
    );
  }

  function getSourcePath(drawer) {
    return (
      (drawer && drawer.getAttribute("data-categories-source-url")) ||
      "/collections/all"
    );
  }

  function getView(drawer) {
    return (drawer && drawer.getAttribute("data-categories-view")) || "categories";
  }

  function findSectionIdInDocument(doc, staticKey) {
    var el =
      doc.querySelector('[id^="shopify-section-"][id$="__' + staticKey + '"]') ||
      doc.querySelector("#shopify-section-" + staticKey);
    if (!el || !el.id) return null;
    return el.id.replace(/^shopify-section-/, "");
  }

  function findSectionIdOnPage(staticKey) {
    return findSectionIdInDocument(document, staticKey);
  }

  async function resolveSectionId(drawer) {
    var staticKey = getStaticSectionKey(drawer);
    var fromPage = findSectionIdOnPage(staticKey);
    if (fromPage) {
      try {
        sessionStorage.setItem(SECTION_ID_STORAGE_KEY, fromPage);
      } catch (e) {}
      return fromPage;
    }

    try {
      var stored = sessionStorage.getItem(SECTION_ID_STORAGE_KEY);
      if (stored && stored.indexOf(staticKey) !== -1) return stored;
    } catch (e) {}

    // Discover instance id from the categories template HTML, then use Section API.
    var discoverUrl = new URL(getSourcePath(drawer), window.location.origin);
    discoverUrl.searchParams.set("view", getView(drawer));
    var res = await fetch(discoverUrl.toString(), { credentials: "same-origin" });
    if (!res.ok) throw new Error("Categories page fetch failed: " + res.status);
    var html = await res.text();
    var doc = new DOMParser().parseFromString(html, "text/html");
    var resolved = findSectionIdInDocument(doc, staticKey);
    if (resolved) {
      try {
        sessionStorage.setItem(SECTION_ID_STORAGE_KEY, resolved);
      } catch (e) {}
      // Also cache extracted root from this HTML to avoid a second request.
      cache.__pageHtml = html;
      return resolved;
    }
    throw new Error("Could not resolve categories section id");
  }

  function extractMainGpFromHtml(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script").forEach(function (node) {
      node.remove();
    });
    return (
      doc.querySelector("[data-categories-tabs-root]") ||
      doc.querySelector(".main_gp")
    );
  }

  function setLoading(drawer, isLoading) {
    var body = drawer.querySelector("[data-categories-drawer-content]");
    if (body) body.setAttribute("aria-busy", isLoading ? "true" : "false");

    var pt = window.MEPageTransition;
    if (!pt) return;

    // Only show the shared page-transition overlay when the drawer is open.
    // Background prefetch must not flash the full-screen loader.
    var drawerOpen = !!(drawer && drawer.hasAttribute("open"));

    if (isLoading && drawerOpen) {
      pt.show();
      drawer.dataset.categoriesPtShown = "true";
      return;
    }

    if (!isLoading && drawer.dataset.categoriesPtShown === "true") {
      pt.hide();
      delete drawer.dataset.categoriesPtShown;
    }
  }

  function mountContent(drawer, sourceRoot) {
    var body = drawer.querySelector("[data-categories-drawer-content]");
    if (!body || !sourceRoot) return false;

    var mount = document.createElement("div");
    mount.className = "main_gp hdt-categories-tabs-root";
    mount.setAttribute("data-categories-tabs-root", "");

    // Prefer raw tab item/content siblings for a clean init.
    var items = sourceRoot.querySelectorAll(".main_gp_tab_item");
    var panels = sourceRoot.querySelectorAll(".main_gp_tab_content");
    if (items.length && panels.length) {
      items.forEach(function (el) {
        mount.appendChild(el.cloneNode(true));
      });
      panels.forEach(function (el) {
        mount.appendChild(el.cloneNode(true));
      });
    } else {
      mount.innerHTML = sourceRoot.innerHTML;
    }

    body.querySelectorAll(".main_gp, .hdt-categories-drawer__error").forEach(function (el) {
      el.remove();
    });
    body.appendChild(mount);

    if (window.HdtCategoriesTabs && typeof window.HdtCategoriesTabs.init === "function") {
      window.HdtCategoriesTabs.init(mount);
    }
    return true;
  }

  function showError(drawer, message) {
    var body = drawer.querySelector("[data-categories-drawer-content]");
    if (!body) return;
    body.querySelectorAll(".hdt-categories-drawer__error").forEach(function (el) {
      el.remove();
    });
    var err = document.createElement("div");
    err.className = "hdt-categories-drawer__error hdt-text-sm";
    err.setAttribute("role", "alert");
    err.textContent = message || "Unable to load categories.";
    body.appendChild(err);
  }

  function cloneFromPage() {
    return document.querySelector(
      ".hdt-categories-all--page [data-categories-tabs-root], .hdt-categories-all--page .main_gp"
    );
  }

  async function fetchSectionHtml(drawer, sectionId) {
    var url = new URL(getSourcePath(drawer), window.location.origin);
    url.searchParams.set("view", getView(drawer));
    url.searchParams.set("sections", sectionId);
    var res = await fetch(url.toString(), { credentials: "same-origin" });
    if (!res.ok) throw new Error("Categories section fetch failed: " + res.status);
    var data = await res.json();
    var html = data[sectionId];
    if (!html) throw new Error("Categories section empty");
    return html;
  }

  async function ensureContent(drawer) {
    if (!drawer) return;
    if (drawer.dataset.categoriesLoaded === "true") return;
    if (loading[drawer.id]) return loading[drawer.id];

    setLoading(drawer, true);

    loading[drawer.id] = (async function () {
      try {
        var pageRoot = cloneFromPage();
        if (pageRoot && pageRoot.querySelector(".main_gp_tab_item, .main_gp_tabs")) {
          mountContent(drawer, pageRoot);
          drawer.dataset.categoriesLoaded = "true";
          return;
        }

        var sectionId = await resolveSectionId(drawer);
        var html = null;

        if (cache.__pageHtml) {
          html = cache.__pageHtml;
          delete cache.__pageHtml;
        } else if (cache[sectionId]) {
          html = cache[sectionId];
        } else {
          try {
            html = await fetchSectionHtml(drawer, sectionId);
          } catch (sectionErr) {
            // Fallback: parse categories page HTML if section API fails.
            var pageUrl = new URL(getSourcePath(drawer), window.location.origin);
            pageUrl.searchParams.set("view", getView(drawer));
            var pageRes = await fetch(pageUrl.toString(), {
              credentials: "same-origin",
            });
            if (!pageRes.ok) throw sectionErr;
            html = await pageRes.text();
          }
        }

        if (!html || html.indexOf("main_gp") === -1) {
          throw new Error("Categories markup not found");
        }

        cache[sectionId] = html;
        var extracted = extractMainGpFromHtml(html);
        if (!extracted) throw new Error("Categories markup not found");
        mountContent(drawer, extracted);
        drawer.dataset.categoriesLoaded = "true";
      } catch (err) {
        console.error("[categories-drawer]", err);
        showError(drawer, "Unable to load categories. Please try again.");
      } finally {
        setLoading(drawer, false);
        delete loading[drawer.id];
      }
    })();

    return loading[drawer.id];
  }

  function onBeforeOpen(event) {
    var drawer = event.currentTarget || getDrawer();
    syncWebToAppOffset(drawer);
    var menu = document.getElementById("menu_mobile");
    if (
      menu &&
      menu !== drawer &&
      menu.hasAttribute("open") &&
      typeof menu.closeDialog === "function"
    ) {
      menu.closeDialog();
    }
    // Show shared me-page-transition loader immediately if content is not ready.
    if (drawer && drawer.dataset.categoriesLoaded !== "true") {
      setLoading(drawer, true);
    }
    ensureContent(drawer);
  }

  function onBeforeClose(event) {
    var drawer = event.currentTarget || getDrawer();
    // Never leave the full-screen transition overlay up after the drawer closes.
    setLoading(drawer, false);
  }

  function getWebToAppSection() {
    return document.querySelector(".web-to-app-pdp-section");
  }

  function measureWebToAppOffset() {
    if (window.matchMedia && !window.matchMedia("(max-width: 767px)").matches) {
      return 0;
    }
    var section = getWebToAppSection();
    if (!section) return 0;
    var wrap = section.querySelector(".web-to-app-pdp-wrap");
    var target = wrap || section;
    var style = window.getComputedStyle(target);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    ) {
      return 0;
    }
    // Use only the portion still covering the top of the viewport.
    // Full height left a blank gap once the banner scrolled away (sticky header).
    var rect = target.getBoundingClientRect();
    if (rect.bottom <= 0) return 0;
    var visibleTop = Math.round(Math.min(rect.height, Math.max(0, rect.bottom)));
    return visibleTop > 0 ? visibleTop : 0;
  }

  function syncWebToAppOffset(drawer) {
    drawer = drawer || getDrawer();
    if (!drawer) return;
    var offset = measureWebToAppOffset();
    var value = offset + "px";
    drawer.style.setProperty("--web-to-app-pdp-offset", value);
    document.documentElement.style.setProperty("--web-to-app-pdp-offset", value);
    if (offset > 0) {
      drawer.setAttribute("data-web-to-app-offset", "true");
    } else {
      drawer.removeAttribute("data-web-to-app-offset");
    }
  }

  function bindDrawer(drawer) {
    if (!drawer || drawer.dataset.categoriesDrawerBound === "true") return;
    drawer.addEventListener("dialog:beforeOpen", onBeforeOpen);
    drawer.addEventListener("dialog:beforeClose", onBeforeClose);
    drawer.addEventListener("dialog:afterClose", onBeforeClose);
    drawer.dataset.categoriesDrawerBound = "true";
    syncWebToAppOffset(drawer);
  }

  document.addEventListener("DOMContentLoaded", function () {
    var drawer = getDrawer();
    bindDrawer(drawer);
    syncWebToAppOffset(drawer);
    if (drawer && "requestIdleCallback" in window) {
      requestIdleCallback(
        function () {
          ensureContent(drawer);
        },
        { timeout: 2500 }
      );
    } else if (drawer) {
      setTimeout(function () {
        ensureContent(drawer);
      }, 1200);
    }
  });

  window.addEventListener("resize", function () {
    syncWebToAppOffset(getDrawer());
  });

  document.addEventListener("shopify:section:load", function (event) {
    if (event.target && event.target.querySelector("#categories_drawer")) {
      bindDrawer(getDrawer());
    }
    syncWebToAppOffset(getDrawer());
  });
})();
